import clsx from "clsx";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { FilterItemType } from "@shared/content-types/types";
import { useOnClickOutside } from "@shared/hooks/use-onclick-outside";
import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { Body } from "@shared/ui/typography/body";

import s from "./filters-dropdown.module.scss";

export type FiltersDropdownProps = ComponentProps<"div"> & {
  className?: string;
  filters: FilterItemType[];
  onFilterClick: (value: string) => void;
  activeFilter: string;
  fieldLabel?: string;
};

export const FiltersDropdown = (props: FiltersDropdownProps) => {
  const { className, filters, onFilterClick, activeFilter, fieldLabel } = props;

  const $rootRef = useRef<HTMLDivElement | null>(null);

  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const toggleOpenDropdown = useCallback(() => {
    setIsOpenDropdown((prev) => !prev);
  }, []);

  const closeDropdown = () => {
    setIsOpenDropdown(false);
  };

  useOnClickOutside($rootRef, [s], closeDropdown);

  useEffect(() => {
    closeDropdown();
  }, [activeFilter]);

  const currentType = useMemo(() => {
    return filters?.find((item) => item.uid === activeFilter);
  }, [activeFilter, filters]);

  const buttonLabel = useMemo(() => {
    const value = currentType?.type || "";
    if (!fieldLabel) return value;
    return `${fieldLabel}: ${value}`;
  }, [currentType?.type, fieldLabel]);

  return (
    <div
      className={clsx(s.root, className, { [s.open]: isOpenDropdown })}
      ref={$rootRef}
    >
      <Button
        className={clsx(s.button, s.mainBtn)}
        onClick={toggleOpenDropdown}
      >
        <Body size="uppercase" weight="semiBold" className={s.label}>
          {buttonLabel}
        </Body>
        <Icon
          name="arrow-down"
          size={24}
          className={clsx(s.arrowIcon, { [s.rotate]: isOpenDropdown })}
        />
      </Button>
      <div
        className={clsx(s.moreList, "lenis-prevent", {
          [s.openDropdown]: isOpenDropdown,
        })}
      >
        {filters.map((item, idx) => (
          <Button
            key={`filter-${item}-${idx + 1}`}
            className={clsx(s.button, {
              [s.activeButton]: activeFilter === item.uid,
            })}
            onClick={() => onFilterClick(item.uid)}
          >
            <Body size="uppercase" weight="semiBold">
              {item.type}
            </Body>
            <Icon
              name="check"
              size={24}
              className={clsx(s.checkIcon, {
                [s.viewIcon]: activeFilter === item.uid,
              })}
            />
          </Button>
        ))}
      </div>
    </div>
  );
};

FiltersDropdown.displayName = "FiltersDropdown";
